import { getAppEndpointVersion } from 'helpers'
import constants from '../constants'

/**
 * Chat-API request helpers (ENG-36335).
 *
 * Centralizes the only two things that differ between calling chat-api directly
 * (region host + appId/apiKey headers) and routing through the mgmt-api
 * ChatApiAPIWrapper (customerDomain + dashboard Bearer JWT). Toggled by the single
 * global flag `constants.useChatApiWrapper`.
 *
 * Usage in a service — the resource + query suffix stays exactly as before:
 *
 *   const URL = `${getChatApiBase(app, apiHost)}/users?per_page=10`
 *   axios.get(URL, { headers: getChatApiHeaders(app) })
 *
 * For endpoints with custom headers, spread then add them so they win:
 *
 *   headers: { ...getChatApiHeaders(app), 'X-Legacy-Webhook': true }
 *
 * NOTE: `useChatApiWrapper` is currently `true` in every environment, so the
 * direct (non-wrapper) branches below are kept for forward-compat but are not
 * exercised today. Likewise, the `apiHost`/`callsHost` args only matter in
 * direct mode.
 *
 * See docs/ENG-36335-chat-api-wrapper-plan.md
 */

/**
 * Base URL up to AND INCLUDING the version segment (e.g. `.../v3.0`).
 * Callers append `/{resource}?{query}` unchanged.
 *
 * @param {object} app      The app object (needs id, version, appRegion.meta.useSSL).
 * @param {string} apiHost  The chat-api region host (used in direct mode only).
 * @returns {string}
 */
export function getChatApiBase(app = {}, apiHost = '') {
  const version = getAppEndpointVersion(app)

  if (constants.useChatApiWrapper) {
    // mgmt-api wrapper: {customerDomain}apps/{appId}/chat-api/{version}
    // customerDomain already ends with a trailing slash.
    return `${constants.customerDomain}apps/${app.id}/chat-api/${version}`
  }

  // direct chat-api: {protocol}{apiHost}/{version}
  const useSSL = app.appRegion && app.appRegion.meta && app.appRegion.meta.useSSL
  const protocol = useSSL ? 'https://' : 'http://'
  return `${protocol}${apiHost}/${version}`
}

/**
 * Auth + default content headers for a chat-api request.
 * Spread this and append any endpoint-specific headers after it.
 *
 * @param {object} app  The app object (needs id, accessKey).
 * @returns {object}
 */
export function getChatApiHeaders(app = {}) {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }

  if (constants.useChatApiWrapper) {
    // mgmt-api validates the dashboard JWT (auth:api + checkUserApp) and injects
    // the region appId/apiKey server-side. Guard on token presence so we never
    // send a literal `Bearer null` (mirrors the apps-list direct mgmt-api call).
    const ccBearerToken = localStorage.getItem('ccBearerToken')
    if (ccBearerToken) headers.Authorization = `Bearer ${ccBearerToken}`
  } else {
    headers.appId = app.id
    headers.apiKey = app.accessKey
  }

  return headers
}

/**
 * Call-API helpers — same idea as the chat-api ones above, but for the Calls API
 * (call logs / sessions). Direct mode hits the per-app calls host
 * ({appId}.{callsHost}/v3.0); wrapper mode routes through the mgmt-api
 * CallApiAPIWrapper at {customerDomain}apps/{appId}/call-api/v3.0. Toggled by its
 * own flag `constants.useCallApiWrapper` (separate from chat-api because the
 * call-api wrapper backend goes live later).
 *
 * @param {object} app        The app object (needs id, appRegion.meta.useSSL).
 * @param {string} callsHost  The calls region host (direct mode only), e.g. call-us.cometchat.io.
 * @returns {string}
 */
export function getCallApiBase(app = {}, callsHost = '') {
  if (constants.useCallApiWrapper) {
    // mgmt-api wrapper: {customerDomain}apps/{appId}/call-api/v3.0
    return `${constants.customerDomain}apps/${app.id}/call-api/v3.0`
  }

  // direct calls host: {protocol}{appId}.{callsHost}/v3.0
  const useSSL = app.appRegion && app.appRegion.meta && app.appRegion.meta.useSSL
  const protocol = useSSL ? 'https://' : 'http://'
  return `${protocol}${app.id}.${callsHost}/v3.0`
}

/**
 * Auth + default content headers for a call-api request.
 * @param {object} app  The app object (needs id, accessKey).
 * @returns {object}
 */
export function getCallApiHeaders(app = {}) {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
  }

  if (constants.useCallApiWrapper) {
    const ccBearerToken = localStorage.getItem('ccBearerToken')
    if (ccBearerToken) headers.Authorization = `Bearer ${ccBearerToken}`
  } else {
    headers.appId = app.id
    headers.apiKey = app.accessKey
  }

  return headers
}
